import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogContent,
  Box, Typography, TextField, IconButton, Alert, CircularProgress,
  Grid, Slide, useMediaQuery, useTheme, LinearProgress, InputAdornment,
} from '@mui/material';
import {
  Close as CloseIcon, CalendarMonth as CalendarIcon,
  Store as StoreIcon, Inventory as ServiceIcon,
  Engineering as WorkerIcon, Rocket as RocketIcon,
  CheckCircle as CheckIcon, ArrowForward as NextIcon,
  ArrowBack as BackIcon, SkipNext as SkipIcon,
  Email as EmailIcon, Phone as PhoneIcon,
  Badge as BadgeIcon, AttachMoney as MoneyIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useEmpresa } from '../../context/EmpresaContext';
import config from '../../config';

const Transition = React.forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));

const STEPS = [
  {
    key: 'periodo', label: 'Periodo', icon: CalendarIcon, color: '#7C3AED',
    title: 'Define tu periodo contable',
    subtitle: 'El mes y año en que se registran tus órdenes',
  },
  {
    key: 'local', label: 'Local', icon: StoreIcon, color: '#0891B2',
    title: 'Registra tu local o sede',
    subtitle: 'Donde tu equipo realiza los servicios',
  },
  {
    key: 'servicio', label: 'Servicio', icon: ServiceIcon, color: '#059669',
    title: 'Agrega tu primer servicio',
    subtitle: 'Lo que tu empresa ofrece a los clientes',
  },
  {
    key: 'trabajador', label: 'Tracker', icon: WorkerIcon, color: '#D97706',
    title: 'Registra a tu primer tracker',
    subtitle: 'Recibirá los servicios en la app móvil',
  },
];

export default function WizardConfiguracion({ open, onClose, onComplete }) {
  const { user } = useAuth();
  const { simboloMoneda } = useEmpresa();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [error, setError] = useState('');
  const [completed, setCompleted] = useState(false);

  // Track which steps already have data (can skip)
  const [stepStatus, setStepStatus] = useState({
    periodo: false, local: false, servicio: false, trabajador: false,
  });

  // Form states
  const now = new Date();
  const [periodo, setPeriodo] = useState({ anio: now.getFullYear(), mes: now.getMonth() + 1, contable: true });
  const [local, setLocal] = useState({ nombre_local: '', direccion_local: '', telefono_local: '' });
  const [servicio, setServicio] = useState({ nombre_servicio: '', descripcion_servicio: '', precio: '' });
  const [trabajador, setTrabajador] = useState({
    nombre_trabajador: '', apellido_trabajador: '', email_trabajador: '',
    telefono_trabajador: '', tipo_documento: 'DNI', numero_documento: '',
  });

  // Plan limits & maestros
  const [planLimits, setPlanLimits] = useState(null);
  const [defaultEscala, setDefaultEscala] = useState(null);
  const [defaultNivel, setDefaultNivel] = useState(null);

  const headers = { Authorization: `Bearer ${user?.token}` };
  const moneda = simboloMoneda || 'S/';

  // Load existing config when wizard opens
  useEffect(() => {
    if (!open) return;
    const loadConfig = async () => {
      setLoadingConfig(true);
      try {
        const [configRes, planRes, escalasRes, nivelesRes] = await Promise.all([
          axios.get(`${config.API_BASE_URL}/api/v1/empresas/estado-configuracion`, { headers }),
          axios.get(`${config.API_BASE_URL}/api/v1/suscripciones/mi-plan`, { headers }).catch(() => null),
          axios.get(`${config.API_BASE_URL}/api/general/escalas`, { headers }).catch(() => null),
          axios.get(`${config.API_BASE_URL}/api/general/niveles-usuario`, { headers }).catch(() => null),
        ]);

        const d = configRes.data?.data;
        if (d) {
          const status = {
            periodo: d.tiene_periodo,
            local: d.tiene_local,
            servicio: d.tiene_servicio,
            trabajador: d.tiene_trabajador,
          };
          setStepStatus(status);

          // Pre-fill period if exists
          if (d.periodo_activo) {
            setPeriodo({ anio: d.periodo_activo.anio, mes: d.periodo_activo.mes, contable: true });
          }

          // Jump to first incomplete step
          const firstIncomplete = STEPS.findIndex(s => !status[s.key]);
          if (firstIncomplete >= 0) setActiveStep(firstIncomplete);
          else setCompleted(true); // all done!
        }

        if (planRes?.data?.data) {
          setPlanLimits(planRes.data.data);
        }

        // Obtener primera escala y nivel de usuario (para crear trabajador)
        const escalas = escalasRes?.data || [];
        if (escalas.length > 0) setDefaultEscala(escalas[0].id_escala);
        const niveles = nivelesRes?.data || [];
        // Buscar nivel "trabajador" o el más alto numéricamente (menos privilegios)
        const nivelTrabajador = niveles.find(n => n.descripcion?.toLowerCase().includes('trabaj')) || niveles[niveles.length - 1];
        if (nivelTrabajador) setDefaultNivel(nivelTrabajador.id_nivel_usuario);
      } catch (e) {
        // Silent
      } finally {
        setLoadingConfig(false);
      }
    };
    loadConfig();
  }, [open]);

  const currentStep = STEPS[activeStep];
  const StepIcon = currentStep?.icon || CalendarIcon;
  const progress = ((Object.values(stepStatus).filter(Boolean).length) / STEPS.length) * 100;

  // Check plan limits
  const isAtLocalLimit = planLimits?.limites?.max_locales != null
    && planLimits.limites.locales_activos >= planLimits.limites.max_locales;
  const isAtWorkerLimit = planLimits?.limites?.max_tecnicos != null
    && planLimits.limites.tecnicos_activos >= planLimits.limites.max_tecnicos;

  const handleNext = async () => {
    setError('');

    // Si el paso ya está completado, solo avanzar sin guardar
    if (stepStatus[currentStep?.key]) {
      if (activeStep < STEPS.length - 1) setActiveStep(prev => prev + 1);
      else { setCompleted(true); localStorage.setItem('wizard_completado', 'true'); }
      return;
    }

    setLoading(true);
    try {
      if (activeStep === 0 && !stepStatus.periodo) {
        if (!periodo.anio || !periodo.mes) throw new Error('Año y mes son obligatorios');
        await axios.post(`${config.API_BASE_URL}/api/periodos`, periodo, { headers });
        setStepStatus(prev => ({ ...prev, periodo: true }));
      } else if (activeStep === 1 && !stepStatus.local) {
        if (isAtLocalLimit) throw new Error(`Tu plan permite máximo ${planLimits.limites.max_locales} local(es)`);
        if (!local.nombre_local) throw new Error('Nombre del local es obligatorio');
        await axios.post(`${config.API_BASE_URL}/api/locales`, local, { headers });
        setStepStatus(prev => ({ ...prev, local: true }));
      } else if (activeStep === 2 && !stepStatus.servicio) {
        if (!servicio.nombre_servicio || !servicio.precio) throw new Error('Nombre y precio son obligatorios');
        await axios.post(`${config.API_BASE_URL}/api/servicios`, servicio, { headers });
        setStepStatus(prev => ({ ...prev, servicio: true }));
      } else if (activeStep === 3 && !stepStatus.trabajador) {
        if (isAtWorkerLimit) throw new Error(`Tu plan permite máximo ${planLimits.limites.max_tecnicos} tracker(s)`);
        if (!trabajador.nombre_trabajador || !trabajador.email_trabajador) {
          throw new Error('Nombre y email son obligatorios');
        }
        await axios.post(`${config.API_BASE_URL}/api/trabajadores`, {
          ...trabajador,
          id_escala: defaultEscala || 1,
          id_nivel_usuario: defaultNivel || 3,
        }, { headers });
        setStepStatus(prev => ({ ...prev, trabajador: true }));
      }

      if (activeStep < STEPS.length - 1) {
        setActiveStep(prev => prev + 1);
      } else {
        setCompleted(true);
        localStorage.setItem('wizard_completado', 'true');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    if (activeStep < STEPS.length - 1) setActiveStep(prev => prev + 1);
  };

  const handleFinish = () => {
    localStorage.setItem('wizard_completado', 'true');
    if (onComplete) onComplete();
    onClose();
  };

  const handleCloseWizard = () => {
    localStorage.setItem('wizard_completado', 'true');
    onClose();
  };

  // ===== COMPLETION SCREEN =====
  if (completed && !loadingConfig) {
    return (
      <Dialog open={open} maxWidth="xs" fullWidth TransitionComponent={Transition}
        PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}>
        <Box sx={{
          background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
          textAlign: 'center', py: 5, px: 3,
        }}>
          <Box sx={{
            width: 80, height: 80, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2,
          }}>
            <RocketIcon sx={{ fontSize: 40, color: '#fff' }} />
          </Box>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#fff', mb: 1 }}>
            ¡Todo listo!
          </Typography>
          <Typography sx={{ fontSize: 14, color: 'rgba(255,255,255,0.85)', mb: 3, lineHeight: 1.5 }}>
            Tu empresa está configurada.
            <br />Crea tu primera orden de servicio y empieza a trackear.
          </Typography>
          <Box
            onClick={handleFinish}
            sx={{
              py: 1.5, px: 4, borderRadius: 3,
              bgcolor: '#fff', color: '#059669',
              fontSize: 15, fontWeight: 800,
              cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 1,
              '&:hover': { transform: 'scale(1.03)' }, transition: 'transform 0.2s',
            }}
          >
            <RocketIcon sx={{ fontSize: 18 }} />
            Crear mi primera orden
          </Box>
        </Box>
      </Dialog>
    );
  }

  // ===== MAIN WIZARD =====
  return (
    <Dialog
      open={open}
      onClose={handleCloseWizard}
      fullScreen={isMobile}
      maxWidth="sm"
      fullWidth
      TransitionComponent={isMobile ? Transition : undefined}
      PaperProps={{ sx: { borderRadius: isMobile ? 0 : 4, overflow: 'hidden' } }}
    >
      {/* Header */}
      <Box sx={{
        background: `linear-gradient(135deg, ${currentStep?.color || '#1A237E'} 0%, ${currentStep?.color || '#1A237E'}CC 100%)`,
        color: '#fff', px: 2.5, py: 2.5, position: 'relative',
      }}>
        <IconButton onClick={handleCloseWizard} sx={{ position: 'absolute', top: 8, right: 8, color: 'rgba(255,255,255,0.7)' }}>
          <CloseIcon />
        </IconButton>

        {/* Progress dots */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          {STEPS.map((s, i) => (
            <Box key={s.key} sx={{
              flex: 1, height: 4, borderRadius: 2,
              bgcolor: stepStatus[s.key] ? '#fff' : (i === activeStep ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.2)'),
              cursor: 'pointer',
              transition: 'all 0.3s',
            }}
              onClick={() => setActiveStep(i)}
            />
          ))}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 48, height: 48, borderRadius: 3,
            bgcolor: 'rgba(255,255,255,0.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <StepIcon sx={{ fontSize: 26 }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 11, opacity: 0.7, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
              Paso {activeStep + 1} de {STEPS.length}
            </Typography>
            <Typography sx={{ fontSize: 18, fontWeight: 800 }}>
              {currentStep?.title}
            </Typography>
            <Typography sx={{ fontSize: 12, opacity: 0.8 }}>
              {currentStep?.subtitle}
            </Typography>
          </Box>
        </Box>

        {/* Done badge */}
        {stepStatus[currentStep?.key] && (
          <Box sx={{
            mt: 1.5, display: 'inline-flex', alignItems: 'center', gap: 0.5,
            bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 2, px: 1.5, py: 0.5,
          }}>
            <CheckIcon sx={{ fontSize: 16 }} />
            <Typography sx={{ fontSize: 12, fontWeight: 700 }}>Ya configurado — puedes continuar</Typography>
          </Box>
        )}
      </Box>

      <DialogContent sx={{ p: 0 }}>
        {loadingConfig ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <CircularProgress size={32} />
            <Typography sx={{ mt: 2, color: '#94A3B8', fontSize: 13 }}>Cargando configuración...</Typography>
          </Box>
        ) : (
          <Box sx={{ p: 2.5 }}>
            {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}

            {/* ===== STEP 0: Periodo ===== */}
            {activeStep === 0 && (
              <Box>
                {stepStatus.periodo && (
                  <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
                    Periodo {periodo.anio}-{String(periodo.mes).padStart(2, '0')} ya creado
                  </Alert>
                )}
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField fullWidth label="Año" type="number" size="small" value={periodo.anio}
                      disabled={stepStatus.periodo}
                      onChange={(e) => setPeriodo(p => ({ ...p, anio: parseInt(e.target.value) }))}
                      sx={{ '& .MuiInputBase-root': { borderRadius: 2 } }} />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField fullWidth label="Mes" type="number" size="small" value={periodo.mes}
                      disabled={stepStatus.periodo}
                      inputProps={{ min: 1, max: 12 }}
                      onChange={(e) => setPeriodo(p => ({ ...p, mes: parseInt(e.target.value) }))}
                      sx={{ '& .MuiInputBase-root': { borderRadius: 2 } }} />
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* ===== STEP 1: Local ===== */}
            {activeStep === 1 && (
              <Box>
                {stepStatus.local && (
                  <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
                    Ya tienes al menos un local registrado
                  </Alert>
                )}
                {isAtLocalLimit && !stepStatus.local && (
                  <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                    Tu plan ({planLimits?.plan?.nombre}) permite máximo {planLimits?.limites?.max_locales} local(es).
                    Ya tienes {planLimits?.limites?.locales_activos}.
                  </Alert>
                )}
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField fullWidth label="Nombre del local" size="small"
                      disabled={stepStatus.local || isAtLocalLimit}
                      value={local.nombre_local}
                      placeholder="Ej: Sede Central, Local San Isidro"
                      InputProps={{ startAdornment: <InputAdornment position="start"><StoreIcon sx={{ fontSize: 18, color: '#94A3B8' }} /></InputAdornment> }}
                      onChange={(e) => setLocal(l => ({ ...l, nombre_local: e.target.value }))}
                      sx={{ '& .MuiInputBase-root': { borderRadius: 2 } }} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth label="Dirección" size="small"
                      disabled={stepStatus.local || isAtLocalLimit}
                      value={local.direccion_local}
                      onChange={(e) => setLocal(l => ({ ...l, direccion_local: e.target.value }))}
                      sx={{ '& .MuiInputBase-root': { borderRadius: 2 } }} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth label="Teléfono" size="small"
                      disabled={stepStatus.local || isAtLocalLimit}
                      value={local.telefono_local}
                      InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ fontSize: 18, color: '#94A3B8' }} /></InputAdornment> }}
                      onChange={(e) => setLocal(l => ({ ...l, telefono_local: e.target.value }))}
                      sx={{ '& .MuiInputBase-root': { borderRadius: 2 } }} />
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* ===== STEP 2: Servicio ===== */}
            {activeStep === 2 && (
              <Box>
                {stepStatus.servicio && (
                  <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
                    Ya tienes servicios registrados
                  </Alert>
                )}
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField fullWidth label="Nombre del servicio" size="small"
                      disabled={stepStatus.servicio}
                      value={servicio.nombre_servicio}
                      placeholder="Ej: Corte de cabello, Limpieza, Reparación"
                      InputProps={{ startAdornment: <InputAdornment position="start"><ServiceIcon sx={{ fontSize: 18, color: '#94A3B8' }} /></InputAdornment> }}
                      onChange={(e) => setServicio(s => ({ ...s, nombre_servicio: e.target.value }))}
                      sx={{ '& .MuiInputBase-root': { borderRadius: 2 } }} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth label="Descripción (opcional)" size="small" multiline rows={2}
                      disabled={stepStatus.servicio}
                      value={servicio.descripcion_servicio}
                      onChange={(e) => setServicio(s => ({ ...s, descripcion_servicio: e.target.value }))}
                      sx={{ '& .MuiInputBase-root': { borderRadius: 2 } }} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth label="Precio base" size="small" type="number"
                      disabled={stepStatus.servicio}
                      value={servicio.precio}
                      InputProps={{ startAdornment: <InputAdornment position="start">{moneda}</InputAdornment> }}
                      onChange={(e) => setServicio(s => ({ ...s, precio: e.target.value }))}
                      sx={{ '& .MuiInputBase-root': { borderRadius: 2 } }} />
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* ===== STEP 3: Trabajador ===== */}
            {activeStep === 3 && (
              <Box>
                {stepStatus.trabajador && (
                  <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
                    Ya tienes trackers registrados
                  </Alert>
                )}
                {isAtWorkerLimit && !stepStatus.trabajador && (
                  <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                    Tu plan ({planLimits?.plan?.nombre}) permite máximo {planLimits?.limites?.max_tecnicos} tracker(s).
                    Ya tienes {planLimits?.limites?.tecnicos_activos}.
                  </Alert>
                )}
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField fullWidth label="Nombres" size="small"
                      disabled={stepStatus.trabajador || isAtWorkerLimit}
                      value={trabajador.nombre_trabajador}
                      onChange={(e) => setTrabajador(t => ({ ...t, nombre_trabajador: e.target.value }))}
                      sx={{ '& .MuiInputBase-root': { borderRadius: 2 } }} />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField fullWidth label="Apellidos" size="small"
                      disabled={stepStatus.trabajador || isAtWorkerLimit}
                      value={trabajador.apellido_trabajador}
                      onChange={(e) => setTrabajador(t => ({ ...t, apellido_trabajador: e.target.value }))}
                      sx={{ '& .MuiInputBase-root': { borderRadius: 2 } }} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth label="Email (acceso a la app)" size="small" type="email"
                      disabled={stepStatus.trabajador || isAtWorkerLimit}
                      value={trabajador.email_trabajador}
                      InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon sx={{ fontSize: 18, color: '#94A3B8' }} /></InputAdornment> }}
                      helperText="Con este email accederá a la app móvil"
                      onChange={(e) => setTrabajador(t => ({ ...t, email_trabajador: e.target.value }))}
                      sx={{ '& .MuiInputBase-root': { borderRadius: 2 } }} />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField fullWidth label="Teléfono" size="small"
                      disabled={stepStatus.trabajador || isAtWorkerLimit}
                      value={trabajador.telefono_trabajador}
                      InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ fontSize: 18, color: '#94A3B8' }} /></InputAdornment> }}
                      onChange={(e) => setTrabajador(t => ({ ...t, telefono_trabajador: e.target.value }))}
                      sx={{ '& .MuiInputBase-root': { borderRadius: 2 } }} />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField fullWidth label="Nro documento" size="small"
                      disabled={stepStatus.trabajador || isAtWorkerLimit}
                      value={trabajador.numero_documento}
                      InputProps={{ startAdornment: <InputAdornment position="start"><BadgeIcon sx={{ fontSize: 18, color: '#94A3B8' }} /></InputAdornment> }}
                      onChange={(e) => setTrabajador(t => ({ ...t, numero_documento: e.target.value }))}
                      sx={{ '& .MuiInputBase-root': { borderRadius: 2 } }} />
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      {/* ===== FOOTER ===== */}
      {!loadingConfig && (
        <Box sx={{ borderTop: '1px solid #E2E8F0', p: 2 }}>
          {/* Progress bar */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <LinearProgress variant="determinate" value={progress}
              sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: '#E2E8F0',
                '& .MuiLinearProgress-bar': { bgcolor: '#10B981', borderRadius: 3 } }} />
            <Typography sx={{ fontSize: 11, color: '#94A3B8', fontWeight: 700 }}>
              {Object.values(stepStatus).filter(Boolean).length}/{STEPS.length}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            {activeStep > 0 && (
              <Box onClick={() => setActiveStep(prev => prev - 1)}
                sx={{
                  py: 1.2, px: 2, borderRadius: 2.5, border: '1.5px solid #E2E8F0',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5,
                  fontSize: 13, fontWeight: 600, color: '#64748B',
                  '&:hover': { bgcolor: '#F8FAFC' },
                }}>
                <BackIcon sx={{ fontSize: 16 }} /> Atrás
              </Box>
            )}

            {!stepStatus[currentStep?.key] && (
              <Box onClick={handleSkip}
                sx={{
                  py: 1.2, px: 2, borderRadius: 2.5,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5,
                  fontSize: 13, fontWeight: 600, color: '#94A3B8',
                  '&:hover': { color: '#64748B' },
                }}>
                <SkipIcon sx={{ fontSize: 16 }} /> Omitir
              </Box>
            )}

            <Box sx={{ flex: 1 }} />

            <Box
              onClick={loading ? undefined : (stepStatus[currentStep?.key] ? (activeStep < STEPS.length - 1 ? () => setActiveStep(p => p + 1) : handleFinish) : handleNext)}
              sx={{
                py: 1.2, px: 3, borderRadius: 2.5,
                background: stepStatus[currentStep?.key]
                  ? 'linear-gradient(135deg, #10B981, #059669)'
                  : `linear-gradient(135deg, ${currentStep?.color}, ${currentStep?.color}CC)`,
                cursor: loading ? 'default' : 'pointer',
                fontSize: 14, fontWeight: 700, color: '#fff',
                display: 'flex', alignItems: 'center', gap: 0.5,
                '&:hover': { transform: loading ? 'none' : 'scale(1.02)' },
                transition: 'transform 0.2s',
              }}
            >
              {loading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : (
                stepStatus[currentStep?.key]
                  ? (activeStep < STEPS.length - 1 ? <>Continuar <NextIcon sx={{ fontSize: 16 }} /></> : <>Finalizar <CheckIcon sx={{ fontSize: 16 }} /></>)
                  : (activeStep === STEPS.length - 1 ? <>Guardar y finalizar <CheckIcon sx={{ fontSize: 16 }} /></> : <>Guardar y continuar <NextIcon sx={{ fontSize: 16 }} /></>)
              )}
            </Box>
          </Box>
        </Box>
      )}
    </Dialog>
  );
}
